import { useCallback, useMemo, useState } from "react";
import { useToken } from "src/business-hooks/useToken";
import { useModal } from "src/components/ConfirmationModal/ConfirmationModal";
import {
  useDeleteApiToken,
  useReactivateApiToken,
  useRevokeApiToken,
} from "src/queries/auth/tokens.mutation";
import { useQueryApiTokens } from "src/queries/auth/tokens.query";
import type { ApiToken } from "src/queries/auth/tokens.types";
import type { TokenMenuAnchor } from "./SystemSettingsTokenManagement.types";

async function confirmTokenAction(
  modal: ReturnType<typeof useModal>,
  token: ApiToken,
  config: {
    title: string;
    messagePrefix: string;
    agree: string;
    color?: "error" | "primary";
  }
): Promise<boolean> {
  const label = token.label || token.jti;
  try {
    await modal.confirm({
      title: config.title,
      message: `${config.messagePrefix} "${label}"?`,
      agree: config.agree,
      color: config.color ?? "error",
    });
    return true;
  } catch {
    return false;
  }
}

export function useSystemSettingsTokenManagement() {
  const { loggedInUser } = useToken();
  // The backend closes token management to read-only sessions entirely.
  const isReadOnly = !!loggedInUser?.read_only;
  const {
    data: tokens,
    isLoading,
    isError,
  } = useQueryApiTokens({ enabled: !isReadOnly });
  const revokeMutation = useRevokeApiToken();
  const reactivateMutation = useReactivateApiToken();
  const deleteMutation = useDeleteApiToken();
  const modal = useModal();
  const [menuAnchor, setMenuAnchor] = useState<TokenMenuAnchor | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);
  const { activeTokens, revokedTokens } = useMemo(() => {
    if (!tokens)
      return { activeTokens: [] as ApiToken[], revokedTokens: [] as ApiToken[] };
    const active: ApiToken[] = [];
    const revoked: ApiToken[] = [];
    for (const token of tokens) {
      (token.is_revoked ? revoked : active).push(token);
    }
    return { activeTokens: active, revokedTokens: revoked };
  }, [tokens]);

  const displayedTokens = useMemo(
    () => (showRevoked ? [...activeTokens, ...revokedTokens] : activeTokens),
    [showRevoked, activeTokens, revokedTokens]
  );

  const handleMenuClose = useCallback(() => setMenuAnchor(null), []);

  const confirmAndMutate = useCallback(
    async (
      token: ApiToken,
      config: Parameters<typeof confirmTokenAction>[2],
      mutate: (params: { jti: string }) => void
    ) => {
      handleMenuClose();
      if (await confirmTokenAction(modal, token, config)) {
        mutate({ jti: token.jti });
      }
    },
    [handleMenuClose, modal]
  );

  const handleRevoke = useCallback(
    (token: ApiToken) =>
      confirmAndMutate(
        token,
        {
          title: "Revoke Token",
          messagePrefix: "Are you sure you want to revoke",
          agree: "Revoke",
          color: "error",
        },
        revokeMutation.mutate
      ),
    [confirmAndMutate, revokeMutation.mutate]
  );

  const handleReactivate = useCallback(
    (token: ApiToken) =>
      confirmAndMutate(
        token,
        {
          title: "Reactivate Token",
          messagePrefix: "Are you sure you want to reactivate",
          agree: "Reactivate",
          color: "primary",
        },
        reactivateMutation.mutate
      ),
    [confirmAndMutate, reactivateMutation.mutate]
  );

  const handleDelete = useCallback(
    (token: ApiToken) =>
      confirmAndMutate(
        token,
        {
          title: "Delete Token Permanently",
          messagePrefix: "Are you sure you want to permanently delete",
          agree: "Delete",
          color: "error",
        },
        deleteMutation.mutate
      ),
    [confirmAndMutate, deleteMutation.mutate]
  );

  const openMenu = useCallback((el: HTMLElement, token: ApiToken) => {
    setMenuAnchor({ el, token });
  }, []);

  return {
    isReadOnly,
    tokens,
    isLoading,
    isError,
    activeTokens,
    revokedTokens,
    displayedTokens,
    showRevoked,
    setShowRevoked,
    menuAnchor,
    handleMenuClose,
    handleRevoke,
    handleReactivate,
    handleDelete,
    openMenu,
  };
}
